/**
 * What Android's back gesture does, as a table rather than as a branch in a listener.
 *
 * Capacitor reports `canGoBack` off the *WebView's* history, which is not a useful signal for a
 * router-driven SPA: a player sitting on the landing page still has entries behind them, and
 * walking back into one bounces off a guard that pushes them straight forward again. So the
 * decision is made from the app's own route instead, and it is made by a pure function that a
 * test can drive without a device.
 *
 * Two of the three routes ask before they act, on the same two-press machinery:
 *
 * - **Leaving the app** is confirmed because a back press at the bottom of the stack is the one
 *   gesture a player makes by accident, and the cost of getting it wrong is the whole session.
 * - **Leaving the arena** is confirmed for a sharper version of the same reason: a match is a
 *   run somebody has been building for minutes, and there is no undo. It is the Leave button's
 *   behaviour, held behind one extra press.
 *
 * Everything else is ordinary history.
 */

/** The bottom of the in-app stack: a back press here is a request to leave the app. */
export const ROOT_ROUTES: readonly string[] = ['/'];

/** The arena. Its own case, because leaving it ends a run rather than changing a page. */
export const ARENA_ROUTE = '/play';

/** How long a first press stays armed for the second one that confirms it. */
export const BACK_CONFIRM_WINDOW_MS = 2_000;

export type BackAction =
  /** Finish the activity and tear the app down. */
  | { kind: 'exit' }
  /** Leave the arena for the landing page — the Leave button's destination. */
  | { kind: 'leave-arena' }
  /** Arm the second press and say so. */
  | { kind: 'confirm'; prompt: string }
  /** Ordinary `history.back()`. */
  | { kind: 'back' };

/**
 * One back press, decided.
 *
 * `armed` is whether a press inside the last BACK_CONFIRM_WINDOW_MS is still standing. The
 * pathname is read at press time by the caller rather than captured when the listener was
 * registered, so a listener registered once can never act on a stale route.
 */
export function decideBack(pathname: string, armed: boolean): BackAction {
  if (pathname === ARENA_ROUTE) {
    return armed
      ? { kind: 'leave-arena' }
      : { kind: 'confirm', prompt: 'Press back again to leave the arena' };
  }
  if (ROOT_ROUTES.includes(pathname)) {
    return armed ? { kind: 'exit' } : { kind: 'confirm', prompt: 'Press back again to exit' };
  }
  return { kind: 'back' };
}
